import { reactive, ref } from 'vue';
import type { Reactive, Ref } from 'vue';
import mime from 'mime';
import { skapi } from '@/main';
import { ENDPOINTS } from '@/code/endpoints';
import { resolveRegion } from './regions';
import { formatSpecs } from '@/code/service-spec';
import { suspensionClock, cancelScheduled } from '@/code/plan-state';
import { dateFormat } from '@/code/utils';
import { nlNewsletterAddress } from '@/code/newsletter-address';
import { SkapiError, type DatabaseResponse } from 'skapi-js';
import { normalizeStoragePath } from '@/code/utils';

const ETC_CONFIG = JSON.parse(import.meta.env.VITE_ETC) as {
	hostDomain: string;
	target_cdn: string;
};
export const hostDomain = ETC_CONFIG?.hostDomain || 'skapi.com';

/**
 * The project's hosting subdomain, or '' when it has none. The `h1-<md5>` name
 * the backend assigns to every new service counts: it is a real address that
 * serves the project's files, so it is shown like one the owner chose. A
 * leading `+`, `*` or `!` is a transit flag (new / releasing / on hold) and is
 * stripped off the name.
 */
export function registeredSubdomain(svc: any): string {
	const raw: string = svc?.service?.subdomain ?? svc?.subdomain ?? (typeof svc === 'string' ? svc : '');
	if (!raw) return '';
	return '+*!'.includes(raw[0]) ? raw.slice(1) : raw;
}
/** True while the subdomain carries a `+`, `*` or `!` transit flag. */
export function subdomainPending(svc: any): boolean {
	const raw: string = svc?.service?.subdomain ?? svc?.subdomain ?? (typeof svc === 'string' ? svc : '');
	return !!raw && '+*!'.includes(raw[0]);
}
export const product_price_code = JSON.parse(import.meta.env.VITE_PRODUCT);

/**
 * True for a db-storage entry that is system-managed and must not be shown in
 * the user's file browser.
 *
 * Reserved names are SHOUTY double-underscored (`__NAME__`), currently just the
 * MCP export namespace `__MCP_EXPORTS__`, which holds transient large-query
 * exports that the MCP server writes and ages out on its own 1-day TTL.
 * `mcp-exports` is the pre-rename name and is matched so already-written
 * exports stay hidden too (they are deliberately not migrated: a download link
 * bakes in its object key, so moving them would break live links).
 *
 * Folders arrive from the backend as `#name`, hence the leading-`#` strip.
 * NOTE: this is UI decluttering, not a permission boundary, a reserved path is
 * still reachable if addressed directly.
 */
export function isReservedDbEntry(name: unknown): boolean {
	if (typeof name !== 'string' || !name) return false;
	const raw = name[0] === '#' ? name.slice(1) : name;
	return /^__.+__$/.test(raw) || raw === 'mcp-exports';
}

export type ServiceFromServer = {
	service: string;
	owner: string;

	name: string; // service name
	cors: string; // http://example.com,https://example.com
	created_locale: string; // country code ex) US. Unchangable after creation.
	description?: string; // service description
	region: string; // aws region code like "us-west-1"

	subs_id: string; // stripe subscription id
	timestamp: number; // created 13 digit timestamp

	active: number; // 1 running, 0 disabled, -1 suspended, -2 deleted, below -100 contained
	group:
		| 1 // "Trial", // currently shown as Standard
		| 2 // "Standard",
		| 3 // "Premium",
		| 50 // "Unlimited",
		| 51 // "Standard (Perpetual License)",
		| 52 // "Premium (Perpetual License)",
		| 61 // "Standard (Promo)", // reverts to 1(Trial) after one month
		| 63 // "Premium (Promo)", // reverts to 1(Trial) after three months
		| 64; // "Premium (Promo)", // reverts to 1(Trial) after six months

	email: string; // owners email

	subdomain: string; // only the subdomain part, not the full url

	users: number; // number of users in this service
	newsletter_subscribers: number; // number of public newsletter subscribers
	serviceletter_subscribers_01?: number; // number of Service Email (group 1) subscribers; absent until the first one

	email_triggers: {
		template_setters: {
			welcome: string;
			confirmation: string;
			newsletter_subscription: string;
			verification: string;
			invitation: string;
		};
	};

	storage_info: {
		database: number;
		cloud: number;
		email: number;
		host: number;
		file: number;
		// Current UTC month counters, joined in by pull-services from the
		// storage row's `<metric>_YYYYMM` attributes. Optional: a region whose
		// pull-services predates them sends none, and the meters must not NaN.
		egress?: number; // bytes served this month
		email_sends?: number; // newsletter mails accepted by SES this month, plus invitation e-mails sent or resent
		mau?: number; // distinct users that produced a token this month
		month?: string; // "YYYY-MM", the month the three counters belong to
	};

	// Overage summary written onto the service row by the OverageMeter job for
	// every service in an overage group. Absent on hard stop plans and before the
	// job's first run; the dashboard reads it as "No overage recorded yet".
	ovg?: ServiceOverage;

	vapid_private_key?: string;
	vapid_public_key?: string;
	api_key: string; // deprecated

	host: string;

	projectId?: string;

	email_alias?: string;
	/** Newsletter subscribers across every group, numeric and named. Absent until the region is backfilled. */
	nlsub?: number;
	prevent_signup?: boolean;
	prevent_inquiry?: boolean;
	freeze_database?: boolean;
	prevent_anonymous?: boolean;
	suspended_at_string?: string;
	suspended_at?: number; // 13 digit timestamp, stamped when active became -1
	// Plan policy v2 suspension attributes, written by the quota evaluator and
	// removed in the same write that moves active off -1. A -1 row without
	// suspend_reason is a legacy or manual suspension.
	suspend_reason?: string; // "quota"
	suspend_over?: string; // comma list drawn from dbst, fpool, emlst (see plan-state suspendOverFromRow)
	egrblk?: string; // "YYYYMM": bandwidth refused that month (only while ENFORCE_EGRESS_HARD_STOP is on)

	client_secret?: { [key: string]: string }; // to be deprecated, moving to separate db
	auth_client_secret?: string[]; // to be deprecated, moving to separate db

	// -- CUSTOM ADDED FIELDS (not from server) --
	will_terminate?: string; // date string of the EARLIEST deletion of a suspended project (suspended_at + 30 days); '' when not suspended, undated, or held by a subscription
};

/** The `ovg` map on a service row, exactly as the OverageMeter job writes it.
 *  `metric` keys are fpool, dbst, emlst, egres, emlsd, mau (and `users` in
 *  `use`/`lim`, which is what mau is measured against). A null limit means
 *  unlimited. Money is integer cents. */
export type ServiceOverage = {
	prd: string; // "YYYY-MM"
	ts: number; // ms of the last sample
	grp: number;
	hrdstp: boolean;
	billable: boolean; // plan allows overage AND a subscription is attached
	use: { [metric: string]: number };
	lim: { [metric: string]: number | null };
	// billed false: metered but not charged (cents is then 0). Absent = billed.
	ovg: { [metric: string]: { units: number; cents: number; billed?: boolean } };
	tot_cents: number;
	rates: { [metric: string]: { cents_per_unit: number; unit: string; billed?: boolean } };
	bill: { status: string; ts: number };
};

export type SubscriptionObj = {
	id: string;
	object: string;
	application: string | null;
	application_fee_percent: number | null;
	automatic_tax: {
		enabled: boolean;
		liability: string | null;
	};
	billing_cycle_anchor: number;
	billing_cycle_anchor_config: object | null;
	billing_thresholds: object | null;
	cancel_at: number | null; // actually cancelled at (10 digit timestamp)
	cancel_at_period_end: boolean;
	canceled_at: number | null; // requested cancel at (10 digit timestamp)
	cancellation_details: {
		comment: string | null;
		feedback: string | null;
		reason: string | null;
	};
	collection_method: string;
	created: number;
	currency: string;
	current_period_end: number;
	current_period_start: number;
	customer: string;
	days_until_due: number | null;
	default_payment_method: string | null;
	default_source: string | null;
	default_tax_rates: string[];
	description: string | null;
	discount: object | null;
	discounts: object[];
	ended_at: number | null;
	invoice_settings: {
		account_tax_ids: string | null;
		issuer: {
			type: string;
		};
	};
	items: object;
	latest_invoice: string;
	livemode: boolean;
	metadata: object;
	next_pending_invoice_item_invoice: string | null;
	on_behalf_of: string | null;
	pause_collection: object | null;
	payment_settings: object;
	pending_invoice_item_interval: object | null;
	pending_setup_intent: string | null;
	pending_update: object | null;
	plan: object;
	quantity: number;
	schedule: object | null;
	start_date: number;
	status: string;
	test_clock: object | null;
	transfer_data: object | null;
	trial_end: number | null;
	trial_settings: object;
	trial_start: number | null;

	// -- CUSTOM ADDED FIELDS (not from server) --
	will_cancel?: string; // date string of when the subscription will be cancelled after cancellation request
};

// One row of the "newsletter-group-endpoint" response.
export type NewsletterGroup = {
	group: string; // the group name
	restriction: number; // 0 public, 1 any signed in user, 2-99 access group >= restriction
	name: string; // display label, optional on the server side
	subscribers: number;
	endpoint: string; // the address this group's newsletters are sent to
};

export const MAX_NEWSLETTER_GROUPS = 20;

const newsletter_group_reserved = [
	'tp',
	'admin',
	'public',
	'authorized',
	'newsletter',
	'forward',
	'all',
];

// The server refuses a bad name with this exact sentence, so the dashboard shows the same one.
export const NEWSLETTER_GROUP_NAME_ERROR =
	'Newsletter group name must be 2-20 lowercase alphanumeric characters, contain a letter, and not be a reserved name.';

export function validateNewsletterGroupName(group: string): string | null {
	if (
		!/^[a-z0-9]{2,20}$/.test(group) ||
		!/[a-z]/.test(group) ||
		newsletter_group_reserved.includes(group)
	) {
		return NEWSLETTER_GROUP_NAME_ERROR;
	}
	return null;
}

// A numeric group addresses itself as "00".."99" everywhere else in the system, a named group as
// its name. Everything the dashboard caches per group is keyed by that token, since a named group
// can not be an array index.
export function newsletterGroupToken(group: number | string): string {
	let token = typeof group === 'number' ? group.toString() : group;
	if (/^\d{1,2}$/.test(token)) {
		return token.padStart(2, '0');
	}
	return token;
}

function randomString(length: number) {
	let result = '';
	let characters =
		'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
	let charactersLength = characters.length;
	for (let i = 0; i < length; i++) {
		result += characters.charAt(
			Math.floor(Math.random() * charactersLength),
		);
	}
	return result;
}

export class Service {
	id: string;
	owner: string;
	admin_private_endpoint: string;
	record_private_endpoint: string;
	record_public_endpoint: string;
	admin_public_endpoint: string;
	service: ServiceFromServer;
	dateCreated: string;
	subscription: SubscriptionObj;

	_subsPromise;
	// keyed by the group token: "00", "01" ... "99" for a numeric group, the name for a named one
	newsletterSender: { [group: string]: Promise<string> } = {};
	// plain array, not a ref: a Service instance is handed out through a reactive ref, which
	// unwraps nested refs on property access
	newsletterGroups: NewsletterGroup[] = [];
	_newsletterGroupsPromise: Promise<NewsletterGroup[]> = null;
	reserved_key: string = randomString(16);
	formattedSpecs: {
		currentPlan: {
			price: number;
			users_string: string;
			storage: {
				database: string;
				cloud: string;
				email: string;
				host: string;
				file: string;
				pool: string;
			};
			storage_bytes: {
				pool: number;
				database: number;
				cloud: number;
				host: number;
				email: number;
				file: number;
			};
			description: string;
			label: string;
			users: number;
			offer_list: Array<{
				class: string;
				text: string;
			}>;
			// Authored per plan, then overwritten by the served hrdstp flag.
			// false = usage past a limit is billed, true = uploads and sends stop.
			hard_stop?: boolean;
			egress?: string;
			egress_bytes?: number;
			email_sends?: number | null;
			email_sends_string?: string;
		};
		serviceStatus: {
			id: string;
			original_service_id: string;
			statusCode: number;
			name: string;
			status: string;
			canChangePlan: boolean;
			users_string: string;
			users_percent: string;
			users: number;
			newsletter_subscribers_string: string;
			newsletter_subscribers: number;
			storage_bytes: {
				database?: number;
				cloud?: number;
				email?: number;
				host?: number;
				file?: number;
			};
			storage: {
				database: string;
				database_percent: string;
				cloud: string;
				cloud_percent: string;
				email: string;
				email_percent: string;
				email_bytes: number;
				email_limit_zero: boolean;
				host: string;
				host_percent: string;
				pool: string;
				pool_bytes: number;
				pool_percent: string;
				segments: {
					host: { bytes: number; text: string; width: string };
					cloud: { bytes: number; text: string; width: string };
					file: { bytes: number; text: string; width: string };
				};
				file: string;
				file_percent: string;
			};
			// This month's metered counters against the plan's monthly limits.
			usage_month: {
				month: string;
				egress: string;
				egress_percent: string;
				email_sends: string;
				email_sends_percent: string;
				mau: string;
			};
			// Derived from service.ovg; null until the OverageMeter job has run.
			overage: {
				month: string;
				sampled: string;
				billable: boolean;
				bill_status: string;
				total: string;
				rows: Array<{
					metric: string;
					label: string;
					used: string;
					included: string;
					over: string;
					units: string;
					cost: string;
				}>;
			} | null;
		};
	};

	constructor(service: ServiceFromServer, endpoints: string[]) {
		this.id = service.service;
		this.admin_private_endpoint = endpoints[0];
		this.record_private_endpoint = endpoints[1];
		this.admin_public_endpoint = endpoints[2];
		this.record_public_endpoint = endpoints[3];

		this.owner = service.owner;
		this.dateCreated =
			typeof service.timestamp === 'string'
				? service.timestamp
				: new Date(service.timestamp).toDateString();

		service.projectId = skapi.util.formatServiceId(
			this.id,
			this.owner,
		);

		this.service = service;

		// NOTE: subdomain info is NOT fetched here. It's a per-project related-data
		// fetch used only by the service views, so firing it in the ctor hit the
		// backend once for EVERY project on the list. It's now fetched on visit
		// (main.vue run() → getSubdomainInfo). The refresh/getInfo path still
		// re-fetches it. getSubdomainInfo is idempotent (getSubdomainInfoTimer guard).

		this.formattedSpecs = formatSpecs(service);
		// NOTE: the per-project Stripe subscription is NOT fetched here anymore.
		// The ctor fired one api.stripe.com request per listed project, and the
		// SDK's shared request queue drains in whole cycles, so entering a
		// project right after the list loaded queued its pull-services /
		// listClientSecrets behind the slowest Stripe call. It's now triggered
		// via ensureStripeSubscription(), deferred by addServiceToList.
	}

	/**
	 * Memoized trigger for the per-project Stripe subscription fetch. Safe to
	 * call any number of times; the first call starts the fetch, later calls
	 * return the same promise.
	 */
	ensureStripeSubscription(): Promise<SubscriptionObj> {
		if (!this._subsPromise) {
			this._subsPromise = this.getStripeSubscription();
		}
		return this._subsPromise;
	}

	/**
	 * Optimistically adjust the displayed db-file storage usage by `deltaBytes`
	 * (positive for a newly uploaded file, negative for a delete) and rebuild
	 * formattedSpecs so the "Usage" figure updates immediately, the server only
	 * refreshes it on a full refresh()/reload. The next authoritative refresh()
	 * overwrites storage_info.file, so any estimate self-corrects. Do NOT call
	 * for EXISTS uploads, a deduped file stores no new bytes.
	 */
	adjustFileStorageUsage(deltaBytes: number) {
		if (!Number.isFinite(deltaBytes) || deltaBytes === 0) return;
		if (!this.service?.storage_info) return;
		const current = this.service.storage_info.file || 0;
		this.service.storage_info.file = Math.max(0, current + deltaBytes);
		this.formattedSpecs = formatSpecs(this.service);
	}

	/**
	 * Same idea for the user tally: the count on the service row is kept by a
	 * backend stream and only reaches this instance on a full reload, so the Users
	 * page moves it by what it just created or deleted. An invitation is not a
	 * user until it is accepted and never calls this.
	 */
	adjustUserCount(delta: number) {
		if (!Number.isFinite(delta) || delta === 0) return;
		if (!this.service) return;
		this.service.users = Math.max(0, (Number(this.service.users) || 0) + delta);
		this.formattedSpecs = formatSpecs(this.service);
	}

	// No alias = removal, like registerSubdomain() with no argument. The backend
	// releases the alias and drops service.email_alias, so sends go out from
	// <service>@mail.skapi.com again.
	async registerSenderEmail(params?: {
		email_alias?: string;
		check?: boolean;
	}): Promise<
		| 'SUCCESS: Sender e-mail has been registered.'
		| 'SUCCESS: E-mail alias is available.'
		| 'SUCCESS: The email alias has been removed.'
	> {
		let emailAlias = params?.email_alias || '';

		if (!emailAlias && params?.check) {
			throw 'Email is required.';
		}

		const specialCharPattern = /[!#$%^&*(),?":{}|<>]/g;
		if (specialCharPattern.test(emailAlias)) {
			throw 'Email contains special characters.';
		}

		let res = await skapi.util.request(
			this.admin_private_endpoint + 'register-sender-email',
			Object.assign(
				{ service: this.id, owner: this.owner },
				{ email_alias: emailAlias, check: params?.check || false },
			),
			{ auth: true },
		);

		if (!params?.check) {
			this.service.email_alias = emailAlias;
		}

		return res;
	}

	registerOpenIDLogger(params: {
		url: string; // openid token 해석 url
		mthd: string; // GET or POST
		hder: { [key: string]: string }; // header
		prms: { [key: string]: string }; // params
		data: any; // post body data (post 만 해당)
		id: string; // openid logger id
		usr: string; // user id로 사용할 openid profile attribute
		cdtn: {
			key: string; // openid profile attribute
			value: string | boolean | number; // openid profile attribute value
			condition: '=' | '>' | '<' | '>=' | '<=' | '!=' | 'ends_with';
			range: string | boolean | number; // value range
		};
		req: 'create' | 'delete' | 'update' | 'list'; // request type
	}) {
		return skapi.util.request(
			this.admin_private_endpoint + 'register-openid',
			Object.assign(
				{ service: this.id, owner: this.owner },
				skapi.util.extractFormData(params).data || {},
			),
			{ auth: true },
		);
	}

	async masterCreateAccount(
		form: any
	) {
		return skapi.createAccount(
			Object.assign({ service: this.id, owner: this.owner }, form)
		);
	}

	async inviteUser(
		form,
		options?: {
			confirmation_url?: string;
			email_subscription?: boolean;
		},
	): Promise<'SUCCESS: Invitation has been sent.'> {
		return skapi.inviteUser(
			Object.assign({ service: this.id, owner: this.owner }, form),
			options,
		);
	}

	async updateUserAttribute(form: { [key: string]: any }): Promise<{ [key: string]: any }> {
		let attributes = skapi.util.extractFormData(form).data;

		await skapi.util.request(
			this.admin_private_endpoint + 'admin-edit-profile',
			Object.assign({ service: this.id, owner: this.owner }, attributes),
			{ auth: true },
		);

		return attributes;
	}

	async grantAccess(params: {
		user_id: string;
		access_group: number;
	}): Promise<'SUCCESS: Access has been granted to the user.'> {
		return skapi.grantAccess(
			Object.assign({ service: this.id, owner: this.owner }, params),
		);
	}

	// get newsletter mail address
	async requestNewsletterSender(params: { group: number | string }): Promise<string> {
		let token = newsletterGroupToken(params.group);

		if (this.newsletterSender[token]) {
			return await this.newsletterSender[token];
		}

		// shown in the "nl" layout whichever one the admin stack still answers with
		let sndr = skapi.util.request(
			this.admin_private_endpoint + 'request-newsletter-sender',
			Object.assign(
				{ service: this.id, owner: this.owner },
				skapi.util.extractFormData(params).data || {},
			),
			{ auth: true },
		).then(nlNewsletterAddress);
		this.newsletterSender[token] = sndr;
		return await sndr;
	}

	async deleteNewsletter(params: { message_id: string; group: number | string }) {
		return skapi.util.request(
			this.admin_private_endpoint + 'delete-newsletter',
			Object.assign(
				{ service: this.id, owner: this.owner },
				skapi.util.extractFormData(params).data || {},
			),
			{ auth: true },
		);
	}

	async blockAccount(
		userId: string,
	): Promise<'SUCCESS: The user has been blocked.'> {
		await skapi.blockAccount(
			Object.assign(
				{ service: this.id, owner: this.owner },
				{ user_id: userId },
			),
		);
		return 'SUCCESS: The user has been blocked.';
	}

	async unblockAccount(
		userId: string,
	): Promise<'SUCCESS: The user has been unblocked.'> {
		await skapi.unblockAccount(
			Object.assign(
				{ service: this.id, owner: this.owner },
				{ user_id: userId },
			),
		);
		return 'SUCCESS: The user has been unblocked.';
	}

	async deleteAccount(
		userId: string,
	): Promise<'SUCCESS: Account has been deleted.'> {
		await skapi.deleteAccount(
			Object.assign(
				{ service: this.id, owner: this.owner },
				{ user_id: userId },
			),
		);
		return 'SUCCESS: Account has been deleted.';
	}

	//send invitation email, when accepted, user will have their account created, and be redirected
	// async resendInvitation(params: { email: string; redirect: string }): Promise<'SUCCESS: Invitation E-Mail has been sent.'> {
	//   let p: any = skapi.util.extractFormData(params).data;
	//   let resend = await skapi.util.request(
	//     'confirm-signup',
	//     {
	//       service: this.id,
	//       owner: this.owner,
	//       is_invitation: p.email,
	//       redirect: p.redirect,
	//     },
	//     { auth: true }
	//   );

	//   return resend; // 'SUCCESS: Invitation E-Mail has been sent.'
	// }

	async getStripeSubscription(refresh = false): Promise<SubscriptionObj> {
		if (this.subscription && !refresh) {
			return this.subscription;
		}

		if (this.service?.subs_id) {
			let service_subs_id = this.service?.subs_id;

			// subs_id 가 배열로 올 경우에는 첫번째 값을 사용
			if (Array.isArray(service_subs_id)) {
				service_subs_id = service_subs_id[0];
			}

			let subs_id = service_subs_id.split('#');

			if (subs_id.length < 2) {
				return;
			}

			let SUBSCRIPTION_ID = subs_id[0];

			this.subscription = await skapi.forwardRequest(null, {
				secretName: 'stripe_test',
				url: `https://api.stripe.com/v1/subscriptions/${SUBSCRIPTION_ID}`,
				method: 'GET',
				headers: {
					Authorization: 'Bearer $CLIENT_SECRET',
					'Content-Type': 'application/x-www-form-urlencoded',
				},
			});

		}

		// Apply the subscription/suspension-derived display fields. Extracted so it
		// can be re-run on the REACTIVE instance after the (now non-blocking) Stripe
		// fetch resolves, see applySubscriptionDerived + addServiceToList.
		this.applySubscriptionDerived();

		return this.subscription;
	}

	// Compute the display fields derived from Stripe subscription / suspension
	// state (will_terminate, will_cancel). Kept separate from the fetch so it can
	// be invoked on the reactive instance (currentService / serviceList[id]) once
	// the background Stripe call lands, so the cancel/past-due banners update
	// without project loading having to block on Stripe.
	//
	// Plan policy v2: a cancel no longer suspends (the project moves to Free at
	// the end of the period), so will_terminate means ONLY the earliest deletion
	// of a suspended project, suspended_at + 30 days, and stays '' while a Stripe
	// subscription holds the deletion. The plan label is never overwritten: the
	// old 'Canceled' label hid the real plan and broke the subscription page.
	applySubscriptionDerived(): void {
		const clock = suspensionClock(this.service, Date.now());
		this.service.will_terminate = clock?.at ? dateFormat(clock.at) : '';
		if (this.subscription) {
			const s = this.subscription;
			s.will_cancel =
				this.service.active === 1 && cancelScheduled(s)
					? dateFormat(s.cancel_at * 1000)
					: '';
		}
	}

	async deleteSubscription(): Promise<SubscriptionObj> {
		if (!this.subscription) {
			return null;
		}

		if (this.service?.subs_id) {
			let service_subs_id = this.service?.subs_id;

			if (Array.isArray(service_subs_id)) {
				service_subs_id = service_subs_id[0];
			}

			let subs_id = service_subs_id.split('#');

			if (subs_id.length < 2) {
				return;
			}

			let SUBSCRIPTION_ID = subs_id[0];

			this.subscription = await skapi.forwardRequest(null, {
				secretName: 'stripe_test',
				url: `https://api.stripe.com/v1/subscriptions/${SUBSCRIPTION_ID}`,
				method: 'DELETE',
				headers: {
					Authorization: 'Bearer $CLIENT_SECRET',
				},
				// // data: {
				// //     cancel_at_period_end: true
				// // }
			});

			this.applySubscriptionDerived();
		}
		return this.subscription;
	}

	async cancelSubscription(): Promise<SubscriptionObj> {
		if (!this.subscription) {
			// subscription 이 없다는 것은 subs_id 가 없다는 것, 즉 구독이 없다는 것
			throw 'no subscription';
		}

		if (this.service?.subs_id) {
			let service_subs_id = this.service?.subs_id;

			if (Array.isArray(service_subs_id)) {
				service_subs_id = service_subs_id[0];
			}

			let subs_id = service_subs_id.split('#');

			if (subs_id.length < 2) {
				return;
			}

			let SUBSCRIPTION_ID = subs_id[0];

			this.subscription = await skapi.forwardRequest(null, {
				secretName: 'stripe_test',
				url: `https://api.stripe.com/v1/subscriptions/${SUBSCRIPTION_ID}`,
				method: 'POST',
				headers: {
					Authorization: 'Bearer $CLIENT_SECRET',
				},
				data: {
					cancel_at_period_end: true,
				},
			});

			this.applySubscriptionDerived();
		}

		return this.subscription;
	}

	async setServiceOption(params: {
		prevent_signup?: boolean;
		prevent_inquiry?: boolean; // true 인 경우 sendInquiry API 사용 불가능
		freeze_database?: boolean; // true 이면 데이터 베이스 구조 고정시키는 기능
		prevent_anonymous?: boolean; // true 인 경우 계정이 없는 사용자는 레코드 생성 불가능
		name?: string;
		cors?: string;
		description?: string;
	}): Promise<ServiceFromServer> {
		let to_update: { [key: string]: any } = {};

		for (let key in params) {
			if (key === 'cors' && params.cors) {
				params.cors = params.cors.trim();
				// if cors have , at the end, remove it
				while (params.cors[params.cors.length - 1] === ',') {
					if (params.cors[params.cors.length - 1] === ',') {
						params.cors = params.cors.slice(0, -1);
						params.cors = params.cors.trim();
					}
				}

				let cors = params.cors.split(',').map((c) => c.trim());
				let service_cors = (this.service.cors || '')
					.split(',')
					.map((c) => c.trim());
				if (
					cors.length === service_cors.length &&
					cors.every((v, i) => v === service_cors[i])
				) {
					return this.service;
				}

				to_update.cors = cors.join(', ');
			} else if (key === 'name' && params.name !== this.service.name) {
				to_update.name = params.name;
			} else if (
				key === 'description' &&
				params.description !== this.service.description
			) {
				to_update.description = params.description;
			} else if (
				params[key as keyof typeof params] !==
				this.service[key as keyof ServiceFromServer]
			) {
				to_update[key] = params[key as keyof typeof params];
			}
		}

		if(Object.keys(to_update).length === 0) {
			return this.service;
		}

		let updated = await skapi.util.request(
			this.admin_private_endpoint + 'service-settings',
			{ service: this.id, owner: this.owner, ...to_update },
			{ auth: true },
		);
		Object.assign(this.service, to_update);
		if (typeof to_update.name === 'string') {
			this.formattedSpecs.serviceStatus.name = to_update.name;
		}
		return updated;
	}

	/**
	 * The deprecated per-service api_key. "service-settings" does not accept it,
	 * so this one field still goes through the legacy register-service update.
	 */
	async updateApiKey(api_key: string): Promise<ServiceFromServer> {
		if (api_key === this.service.api_key) {
			return this.service;
		}

		await skapi.util.request(
			this.admin_private_endpoint + 'register-service',
			{
				execute: 'update',
				service: this.id,
				owner: this.owner,
				api_key,
			},
			{ auth: true },
		);

		this.service.api_key = api_key;
		return this.service;
	}

	async registerClientSecret(params: {
		id: string | string[]; // client secret key id. should be array when req is delete
		key?: string; // client secret key value. required when req is create or update
		grp?: number; // access group number. 0 for public, 1 for locked.
		allowed_destination?: string[] | string; // URLs this secret may be sent to, as a list or comma-separated. Empty on update removes the restriction.
		req: 'create' | 'delete' | 'update';
	}): Promise<{
		id: string | string[]; // client secret key id. should be array when req is delete
		key?: string; // client secret key value. required when req is create or update
		grp?: number; // access group number. 0 for public, 1 for locked.
		req: 'create' | 'delete' | 'update';
	}> {
		if (
			params.req === 'delete' &&
			typeof params.id === 'string' &&
			!Array.isArray(params.id)
		) {
			params.id = [params.id];
		}

		await skapi.util.request(
			this.admin_private_endpoint + 'register-client-secret',
			Object.assign(
				{ service: this.id, owner: this.owner },
				skapi.util.extractFormData(params).data || {},
			),
			{ auth: true },
		);

		return params;
	}

	async listClientSecrets(fetchMore: boolean = false, search?: string): Promise<
		DatabaseResponse<{
			grp: number;
			key: string;
			id: string;
		}>
	> {
		let secrets = await skapi.util.request(
			this.admin_private_endpoint + 'register-client-secret',
			{ service: this.id, owner: this.owner, req: 'list', id: search || '' },
			{
				fetchOptions: {
					limit: 120,
					fetchMore: fetchMore,
					ascending: true,
				},
				method: 'post',
				auth: true,
			},
		);
		return secrets;
	}

	// Tickets (dev/TICKETS_SPEC.md section 2). Thin on purpose: the endpoint
	// validates, and the page's tickets.ts owns the shapes.
	registerTicket(params: { exec: 'reg' | 'unreg'; ticket_id: string; [key: string]: any }): Promise<any> {
		return skapi.util.request(
			this.admin_private_endpoint + 'register-ticket',
			Object.assign({ service: this.id, owner: this.owner }, params),
			{ auth: true },
		);
	}

	unregisterTicket(ticket_id: string): Promise<string> {
		return this.registerTicket({ exec: 'unreg', ticket_id });
	}

	// `startKey` goes in the body as the endpoint's own cursor; resolves to the
	// raw `{ list, startKey, ... }`.
	getTickets(params?: {
		ticket_id?: string;
		ascending?: boolean;
		limit?: number;
		startKey?: any;
	}): Promise<{ list: any[]; startKey?: any; endOfList?: boolean }> {
		return skapi.util.request(
			this.admin_private_endpoint + 'ticket',
			Object.assign({ service: this.id, owner: this.owner, exec: 'list' }, params || {}),
			{ auth: true },
		);
	}

	async getEmailTemplate(
		params:
			| 'invitation'
			| 'newsletter_subscription'
			| 'verification'
			| 'welcome'
			| 'confirmation',
	): Promise<{
		subject: string;
		url: string;
	}> {
		let updated = await skapi.util.request(
			this.admin_private_endpoint + 'email-templates',
			{ service: this.id, owner: this.owner, template: params },
			{ auth: true },
		);
		return updated;
	}

	async enableService(): Promise<ServiceFromServer> {
		if (this.service.active === 0) {
			this.service.active = 1;
			try {
				await skapi.util.request(
					this.admin_private_endpoint + 'create-service',
					{
						service: this.id,
						owner: this.owner,
						execute: 'enable',
					},
					{ auth: true },
				);
			} catch (err: any) {
				this.service.active = 0;
				throw err;
			}
		}

		return this.service;
	}

	async disableService(): Promise<ServiceFromServer> {
		if (this.service.active > 0) {
			let orgActive = this.service.active;
			this.service.active = 0;

			try {
				await skapi.util.request(
					this.admin_private_endpoint + 'create-service',
					{
						service: this.id,
						owner: this.owner,
						execute: 'disable',
					},
					{ auth: true },
				);
			} catch (err: any) {
				this.service.active = orgActive;
				throw err;
			}
		}

		return this.service;
	}

	async deleteService(): Promise<void> {
		return skapi.util.request(
			this.admin_private_endpoint + 'create-service',
			{
				service: this.id,
				owner: this.owner,
				execute: 'delete',
			},
			{ auth: true },
		);
		// need servicelist removal from outside
	}

	async getMailTemplates(
		params?: {
			searchFor:
				| 'message_id'
				| 'timestamp'
				| 'read'
				| 'complaint'
				| 'subject';
			value: string | number;
			range?: string | number;
			condition?:
				| '>'
				| '>='
				| '='
				| '<'
				| '<='
				| 'gt'
				| 'gte'
				| 'eq'
				| 'lt'
				| 'lte';
			group:
				| 'welcome'
				| 'confirmation'
				| 'verification'
				| 'invitation'
				| 'newsletter_subscription';
		},
		fetchOptions?: { [key: string]: any },
	): Promise<any> {
		try {
			let res = await skapi.util.request(
				this.admin_private_endpoint + 'get-templates',
				Object.assign(
					{
						service: this.id,
						owner: this.owner,
					},
					params,
				),
				{ auth: true, method: 'get', fetchOptions: fetchOptions || {} },
			);

			res.list = res.list.map((value: any) => {
				return {
					message_id: value.mid,
					timestamp: value.stmp,
					subject: value.subj,
					url: value.url,
				};
			});

			return res;
		} catch (err: any) {
			throw err;
		}
	}

	async deleteTemplate(params?: {
		message_id: string;
		group:
			| 'welcome'
			| 'confirmation'
			| 'verification'
			| 'invitation'
			| 'newsletter_subscription';
	}): Promise<any> {
		try {
			return skapi.util.request(
				this.admin_private_endpoint + 'delete-template',
				Object.assign(
					{
						service: this.id,
						owner: this.owner,
					},
					params,
				),
				{ auth: true },
			);
		} catch (err: any) {
			throw err;
		}
	}

	async setTemplate(params?: {
		message_id: string;
		group:
			| 'welcome'
			| 'confirmation'
			| 'verification'
			| 'invitation'
			| 'newsletter_subscription';
	}): Promise<any> {
		try {
			return skapi.util.request(
				this.admin_private_endpoint + 'set-template',
				Object.assign(
					{
						service: this.id,
						owner: this.owner,
					},
					params,
				),
				{ auth: true },
			);
		} catch (err: any) {
			throw err;
		}
	}

	pending: {
		subdomain: false | 'removing' | 'transit' | 'fetching' | string;
		cdn: boolean; // true when cdn refresh is in process
	} = reactive({
		subdomain: false,
		cdn: false,
	});

	subdInfo = reactive({
		srvc: '',
		subd: '',
		ownr: '',
		stat: '',
		['404']: '...',
	});

	getSubdomainInfoTimer: any = null;
	// Generation token: a subdomain-info answer that was requested before a
	// register/change/remove must not overwrite the flagged name the server
	// just returned, nor touch the newer poll's timer.
	private subdInfoGen = 0;

	dirInfo: Reactive<{
		cnt: number;
		size: number;
		upl: number;
		path: string;
	}> = reactive({
		cnt: 0,
		size: 0,
		upl: 0,
		path: '',
	});

	dbDirInfo: Reactive<{
		cnt: number;
		size: number;
		upl: number;
		path: string;
	}> = reactive({
		cnt: 0,
		size: 0,
		upl: 0,
		path: '',
	});
	
	async getSubdomainInfo(interval: number = 1000): Promise<{
		srvc: string;
		subd: string;
		ownr: string;
		stat:
			| 'remove'
			| 'change:(subdomain to be changed to)'
			| 'active'
			| string;
		['404']?: string;
	} | null> {
		if (this.getSubdomainInfoTimer) {
			return null;
		}

		const gen = ++this.subdInfoGen;
		this.pending.subdomain = 'fetching';

		let exec = async (): Promise<any> => {
			if (!this.service.service && this.service.subdomain) {
				Object.assign(this.subdInfo, {
					srvc: this.id,
					subd: '',
					ownr: this.owner,
					stat: '',
					['404']: '',
				});

				this.pending.subdomain = false;
				return this.subdInfo;
			}

			let info: any;

			try {
				info = await skapi.util.request(
					this.admin_private_endpoint + 'subdomain-info',
					{ service: this.id, owner: this.owner },
					{ auth: true },
				);
			} catch (err) {
				if (gen !== this.subdInfoGen) {
					// superseded by a newer register/change/remove
					return;
				}
				// retry on error
				interval *= 1.2;
				this.getSubdomainInfoTimer = setTimeout(exec, interval);
				return;
			}

			if (gen !== this.subdInfoGen) {
				// superseded: do not touch service.subdomain or the newer poll's timer
				return;
			}

			if (
				typeof info === 'string' ||
				!info ||
				(typeof info === 'object' && Object.keys(info).length === 0)
			) {
				// subdomain removed
				clearTimeout(this.getSubdomainInfoTimer);
				this.getSubdomainInfoTimer = null;
				this.pending.subdomain = false;
				this.service.subdomain = null;

				Object.assign(this.subdInfo, {
					srvc: this.id,
					subd: '',
					ownr: this.owner,
					stat: '',
					['404']: '',
				});

				return this.subdInfo;
			}

			if (info.invid) {
				this.checkCDNStatus(true);
			}

			if (info.stat) {
				// banned | remove | change:(subdomain to be changed to) | active | tracked | needs_refresh
				let pendingStat = info.stat.includes('change:')
					? 'transit'
					: info.stat.includes('remove')
						? 'removing'
						: info.stat === 'active' ||
							  info.stat === 'tracked' ||
							  info.stat === 'needs_refresh' ||
							  info.stat === 'banned'
							? false
							: info.stat;

				this.pending.subdomain = pendingStat;

				if (pendingStat) {
					// is transit or removing - keep polling
					interval *= 1.2;
					this.subdInfo.stat = info.stat;
					this.getSubdomainInfoTimer = setTimeout(exec, interval);
				} else {
					// reached final state
					clearTimeout(this.getSubdomainInfoTimer);
					this.getSubdomainInfoTimer = null;

					this.pending.subdomain = false;
					this.service.subdomain = info.subd;
					if (!info?.['404']) {
						delete this.subdInfo['404'];
					}
					Object.assign(this.subdInfo, info);
					return info;
				}
			} else {
				// no stat in response - retry
				interval *= 1.2;
				this.getSubdomainInfoTimer = setTimeout(exec, interval);
			}
		};

		this.getSubdomainInfoTimer = true; // guard before first async call
		return exec();
	}

	async registerSubdomain(params?: {
		subdomain?: string;
		check?: boolean;
	}): Promise<ServiceFromServer | { status: 'success'; resp: any }> {
		let { subdomain = '', check = false } = params || {};
		this.reserved_key = randomString(16);

		if (this.service?.subdomain === subdomain) {
			return this.service;
		}

		if (this.service?.subdomain) {
			if (subdomain && this.service?.subdomain[0] === '*') {
				throw 'Previous subdomain is in removal process.';
			} else if (subdomain && this.service?.subdomain[0] === '+') {
				throw `Previous subdomain is in transit to "${this.service.subdomain.slice(
					1,
				)}".`;
			}
		}

		// this.pending.subdomain = subdomain ? 'pending' : 'removing';
		// this.subdInfo.stat = subdomain ? 'pending' : 'removing';
		let resp = await skapi.util.request(
			this.admin_private_endpoint + 'register-subdomain',
			{ service: this.id, owner: this.owner, subdomain, check },
			{
				auth: true,
				method: 'post',
			},
		);

		// if (typeof resp !== 'string') {
		// 	this.service.subdomain = resp.subdomain;
		// 	this.subdInfo.stat = 'pending';
		// }

		if (params?.check) {
			return { status: 'success', resp }; // string, { body: string }
		}

		if (resp && typeof resp === 'object') {
			// The register/change/remove answer is the bare service-table row (no
			// projectId / will_terminate, which only the ctor and
			// applySubscriptionDerived write). Merge it in place instead of swapping
			// so those client-side fields survive. The two edge replies are plain
			// strings ('already registered', 'is removed') and carry nothing to merge.
			const { projectId, will_terminate } = this.service;
			Object.assign(this.service, resp, { projectId, will_terminate });
		}

		// Drop any poll that was already running: its answer predates this change
		// and must not overwrite the flagged name the server just returned.
		// (clearTimeout(true) is harmless when the guard holds the sentinel; the
		// generation bump inside getSubdomainInfo invalidates the in-flight exec.)
		clearTimeout(this.getSubdomainInfoTimer);
		this.getSubdomainInfoTimer = null;
		await this.getSubdomainInfo();
		return this.service;
	}

	async fetchCDNStatus() {
		return await skapi.util.request(
			this.admin_private_endpoint + 'refresh-cdn',
			{
				service: this.id,
				owner: this.owner,
				exec: 'status',
			},
			{
				auth: true,
				method: 'post',
			},
		);
	}

	cdnPendTimer: any = null;
	checkCDNStatus(immediate?: boolean) {
		if (this.cdnPendTimer) {
			return;
		}

		this.pending.cdn = true;
		// Returns the timer id so cdnPendTimer is truthy while a poll is scheduled;
		// the re-entry guard above and the clearTimeout calls depend on it.
		let callbackInterval = (
			immediate = false,
		): ReturnType<typeof setTimeout> => {
			let time = immediate ? 1 : 30000;
			return setTimeout(() => {
				this.fetchCDNStatus()
					.then((res) => {
						if (res === 'COMPLETE') {
							this.pending.cdn = false;
							clearTimeout(this.cdnPendTimer);
							this.cdnPendTimer = null;
							this.subdInfo.stat = 'tracked';

							return;
						}
						this.cdnPendTimer = callbackInterval();
					})
					.catch((err) => {
						this.pending.cdn = false;
						clearTimeout(this.cdnPendTimer);
						this.cdnPendTimer = null;
						return;
					});
			}, time);
		};

		this.cdnPendTimer = callbackInterval(immediate);
	}

	async refreshCDN(): Promise<
		| 'IS_QUEUED' // new cdn refresh is queued
		| 'IN_QUEUE' // the previous cdn refresh is still in queue
		| 'IN_PROCESS' // the cdn refresh is in process
	> {
		if (this.pending.cdn) {
			return 'IN_PROCESS';
		}

		if (!this.service.subdomain || this.service.subdomain[0] === '*') {
			throw 'Subdomain does not exists.';
		}

		try {
			this.pending.cdn = true;
			await skapi.util.request(
				this.admin_private_endpoint + 'refresh-cdn',
				{
					service: this.id,
					owner: this.owner,
					exec: 'refresh',
				},
				{
					auth: true,
					method: 'post',
				},
			);

			this.checkCDNStatus();
			return 'IS_QUEUED';
		} catch (err: any) {
			// A refresh is already running server-side. pending.cdn stays true so the
			// button stays disabled; start the status poll so the flag clears on its
			// own once that earlier refresh completes.
			if (err.message === 'Previous cdn refresh is still in queue.') {
				this.checkCDNStatus();
				return 'IN_QUEUE';
			}
			if (err.message === 'Previous cdn refresh is in process.') {
				this.checkCDNStatus();
				return 'IN_PROCESS';
			} else {
				this.pending.cdn = false;
				if (this.cdnPendTimer) {
					clearTimeout(this.cdnPendTimer);
					this.cdnPendTimer = null;
				}
				throw err;
			}
		}
	}

	async set404(params: {
		path: string; // Set path to file of 404 page. ex) folder/file.html
	}): Promise<'SUCCESS: Removed 404 page'> {
		return await skapi.util.request(
			this.admin_private_endpoint + 'set-404',
			{ service: this.id, owner: this.owner, page404: params.path },
			{ auth: true },
		);
	}

	async uploadHostFiles(
		fileList: FormData | HTMLFormElement | SubmitEvent,
		params: {
			contentTypeMapping?: { [fileName: string]: string };
			prefix?: string;
			target?: 'db' | 'host';
			check_existence?: boolean; // check existence of file before upload, if true, it will skip the file that already exists in the target storage
			progress?: (p: {
				status: string; // 'loadstart' | 'progress' | 'abort' | 'error' | 'load' | 'timeout' | 'loadend',
				progress: number;
				currentFile: File;
				completed: File[];
				failed: File[];
				loaded: number;
				total: number;
				abort: () => void;
			}) => void;
		},
	): Promise<{ completed: File[]; failed: File[]; bin_endpoints: string[] }> {
		let progress = params?.progress || ((p: any) => p);

		if (fileList instanceof SubmitEvent) {
			fileList = fileList.target as HTMLFormElement;
		}

		if (fileList instanceof HTMLFormElement) {
			fileList = new FormData(fileList);
		}

		let reserved_key = this.reserved_key;

		let getSignedParams: Record<string, any> = {
			reserved_key,
			service: this.id,
			owner: this.owner,
			request: params?.target || 'host',
		};

		if (params?.target === 'host') {
			getSignedParams.id = this.service.subdomain;
		}

		if (params?.check_existence) {
			getSignedParams.check_existence = true;
		}

		let xhr: any;
		let fetchProgress = (
			url: string,
			body: FormData,
			progressCallback: (p: ProgressEvent) => void,
		) => {
			return new Promise((res, rej) => {
				xhr = new XMLHttpRequest();
				xhr.open('POST', url);
				xhr.onload = () => {
					let result = xhr.responseText;
					try {
						result = JSON.parse(result);
					} catch (err) {}
					if (xhr.status >= 200 && xhr.status < 300) {
						res(result);
					} else {
						rej(result);
					}
				};
				xhr.onerror = () => rej('Network error');
				xhr.onabort = () => rej('Aborted');
				xhr.ontimeout = () => rej('Timeout');

				// xhr.addEventListener('error', rej);
				if (xhr.upload && typeof progressCallback === 'function') {
					xhr.upload.onprogress = progressCallback;
				}

				xhr.onprogress = progressCallback;

				xhr.send(body);
			});
		};

		let completed = [];
		let failed = [];
		// The real per-file upload error, parallel to `failed`. Previously the
		// S3 POST rejection was discarded, so a failed file upload surfaced as
		// a bare "Upload failed" with no cause, undiagnosable (e.g. the one .xls
		// that "won't upload no matter what"). Callers can now report the reason.
		let failedErrors: any[] = [];
		let bin_endpoints = [];
		let prefix = params.prefix ? params.prefix : '';
		prefix =
			prefix && prefix[prefix.length - 1] === '/' ? prefix : prefix + '/';

		for (let [key, f] of (fileList as any).entries()) {
			if (!(f instanceof File)) {
				continue;
			}

			// Safety net. Callers compose at capture (see normalizeStoragePath), but this is the
			// one place every upload funnels through, so compose here too rather than trust that
			// every present and future caller remembered.
			let rawName = normalizeStoragePath(f.webkitRelativePath || f.name);
			let fn = rawName.split('/');
			let fns = fn[fn.length - 1];
			// Look the Content-Type up under BOTH forms. The mapping is built by the caller from
			// its own copy of the name; if that copy is decomposed and this one is composed, a
			// single-form lookup silently drops the Content-Type for every non-ASCII filename and
			// the file is served as application/octet-stream.
			let ct =
				params?.contentTypeMapping?.[fns] ||
				params?.contentTypeMapping?.[fns.normalize('NFD')] ||
				null;

			let signedParams = Object.assign(
				{
					key: prefix + rawName,
					size: f.size || 0,
					contentType: f.type || ct,
				},
				getSignedParams,
			);

			let { fields = null, url } = await skapi.util.request(
				this.record_private_endpoint + 'get-signed-url',
				signedParams,
				{ auth: true },
			);

			bin_endpoints.push(url);

			let form = new FormData();

			for (let name in fields) {
				form.append(name, fields[name]);
			}

			form.append('file', f);

			try {
				await fetchProgress(url, form, (p: ProgressEvent) =>
					progress({
						status: p.type,
						progress: (p.loaded / p.total) * 100,
						currentFile: f,
						completed,
						failed,
						loaded: p.loaded,
						total: p.total,
						abort: () => xhr.abort(),
					}),
				);
				completed.push(f);
			} catch (err) {
				failed.push(f);
				failedErrors.push(err);
			}
		}

		return { completed, failed, failedErrors, bin_endpoints };
	}

	async deleteHostFiles(params: {
		target?: 'db' | 'host';
		paths: string[]; // path without subdomain ex) folder/file.html
	}): Promise<string> {
		if (!params.paths || !params.paths.length) return;
		return skapi.util.request(
			this.record_private_endpoint + 'del-files',
			{
				service: this.id,
				owner: this.owner,
				endpoints: params.paths,
				reserved_key: this.reserved_key,
				storage: params.target || 'host',
			},
			{ auth: true, method: 'post' },
		);
	}

	async getDirInfo(params?: { target: 'db' | 'host' }): Promise<{
		name: string; // !
		path: string; // subdomain
		size: number;
		upl: number;
		cnt: number;
	}> {
		let dirInfo = await skapi.util.request(
			this.admin_private_endpoint + 'host-directory',
			Object.assign(
				{ service: this.id, owner: this.owner },
				{ dir: '', info: true, target: params?.target || 'host' },
			),
			{
				fetchOptions: {
					limit: 1,
					fetchMore: false,
					ascending: true,
				},
				method: 'post',
				auth: true,
			},
		);

		if (dirInfo) {
			if (params?.target === 'db') {
				Object.assign(this.dbDirInfo, dirInfo);
			} else {
				Object.assign(this.dirInfo, dirInfo);
			}
		}
		return dirInfo;
	}

	async listHostDirectory(
		params: {
			dir: string; // unix style dir with subdomain. ex) subdomain/folder/subfolder
			target?: 'db' | 'host';
		},
		fetchMore: boolean = false,
		limit: number = 120,
	): Promise<{
		[key: string]: any;
		list: {
			name: string; // file name file.txt | #folder
			path: string; // file path ex) subdomain/folder/subfolder
			size: number;
			upl: number;
			cnt: number;
		}[];
	}> {
		let _subd = this.service?.subdomain;
		if (_subd && (_subd[0] === '*' || _subd[0] === '+')) {
			_subd = _subd.slice(1);
		}

		let dirList = await skapi.util.request(
			this.admin_private_endpoint + 'host-directory',
			Object.assign({ service: this.id, owner: this.owner }, params),
			{
				fetchOptions: {
					limit,
					fetchMore: !!fetchMore,
					ascending: true,
				},
				method: 'post',
				auth: true,
			},
		);

		// Hide system-managed db folders (e.g. the MCP export namespace) from the
		// user's file browser. Filtered HERE because this is the single choke
		// point every db consumer goes through, so no listing can see or descend
		// into them.
		// `rawCount` is preserved so callers can tell "the server returned nothing"
		// (real end of directory) from "everything on this page was filtered out",
		// which would otherwise falsely terminate their paging loop.
		// The host target (subdomain hosting) is deliberately untouched.
		if (params?.target === 'db' && Array.isArray(dirList?.list)) {
			dirList.rawCount = dirList.list.length;
			dirList.list = dirList.list.filter((i: any) => !isReservedDbEntry(i?.name));
		}
		return dirList;
	}

	async getTemporaryUrl(params: {
		request: 'get-host' | 'get-db' | 'get-db-zip' | 'get-host-zip';
		path: string;
		contentType?: string;
		expires: number; // seconds
		generate_redirect_url?: boolean; // generate redirect url after upload, if true, it will return the url of the uploaded file in the response
		generate_temporary_cdn_url?: boolean; // generate temporary cdn url for the uploaded file, if true, it will return the cdn url in the response
	}): Promise<{ url: string }> {
		let request = params.request || 'get-host';
		let key = params.path || '';
		let expires = params.expires || 3600;
		let contentType = params.contentType || 'application/octet-stream';
		let generate_redirect_url = params.generate_redirect_url || false;
		let generate_temporary_cdn_url = params.generate_temporary_cdn_url || false;
		// set contentType based on file extension if contentType is not provided
		if (!params.contentType) {
			contentType = mime.getType(key) || contentType;
		}

		return skapi.util
			.request(
				this.record_private_endpoint + 'get-signed-url',
				Object.assign(
					{ service: this.id, owner: this.owner },
					{
						request,
						key,
						expires,
						contentType,
						generate_redirect_url,
						generate_temporary_cdn_url,
					},
				),
				{
					auth: true,
					method: 'post',
				},
			)
			.then((tempUrl: any) => {
				let filename = key.split('/').pop();
				if(filename[0] === '#') {
					filename = filename.slice(1) + '.zip';
				}
				if (request === 'get-db-zip' || request === 'get-host-zip') {
					if (tempUrl?.tmp_id) {
						let tempId = tempUrl.tmp_id;
						return this.fetchTmpZip({ key: tempId, expires, filename });
					}
				}
				// A suspended or contained project answers a platform account or a super
				// admin with a plain presigned url instead of a redirect or CDN url, so
				// that the owner can still download while the edge refuses everyone else.
				// It is already absolute: prefixing it would 404.
				if (tempUrl?.absolute) {
					return tempUrl;
				}
				if(generate_redirect_url) {
					tempUrl.url = this.record_public_endpoint + tempUrl.url;
				}
				else if(generate_temporary_cdn_url) {
					tempUrl.url = `https://db.${hostDomain}/` + tempUrl.url;
				}
				return tempUrl;
			});
	}

	async fetchTmpZip(params: {
		key: string;
		expires: number;
		filename: string;
	}): Promise<{ url: string }> {
		try {
			let resp = await skapi.util.request(
				this.record_private_endpoint + 'get-signed-url',
				Object.assign(
					{ service: this.id, owner: this.owner },
					{
						request: 'get-tmp',
						key: params.key,
						expires: params.expires,
						contentType: mime.getType(params.filename) || 'application/zip',
						filename: params.filename,
					},
				),
				{ auth: true, method: 'post' },
			);
			return resp;
		} catch (err) {
			if (err instanceof SkapiError && err.code === 'NOT_EXISTS') {
				// recurring check until the zip file is ready, with 5 seconds interval
				await new Promise((res) => setTimeout(res, 5000));
				return this.fetchTmpZip(params);
			} else {
				throw err;
			}
		}
	}
	// -------------------------------- vivians area --------------------------------

	async registerNewsletterGroup(params: {
		group: string;
		restriction: number;
		name?: string;
	}): Promise<string> {
		let res = await skapi.util.request(
			this.admin_private_endpoint + 'register-newsletter-group',
			Object.assign({ service: this.id, owner: this.owner }, params),
			{ auth: true },
		);

		// the new group's address and subscriber count come from the group listing,
		// never from a second mint request
		await this.listNewsletterGroups(true).catch(() => { });

		return res;
	}

	async deleteNewsletterGroup(params: { group: string }): Promise<string> {
		let res = await skapi.util.request(
			this.admin_private_endpoint + 'delete-newsletter-group',
			Object.assign({ service: this.id, owner: this.owner }, params),
			{ auth: true },
		);

		delete this.newsletterSender[newsletterGroupToken(params.group)];
		this.newsletterGroups = this.newsletterGroups.filter(
			(g) => g.group !== params.group,
		);
		this._newsletterGroupsPromise = Promise.resolve(this.newsletterGroups);

		return res;
	}

	async newsletterGroupEndpoint() {
		let response = await skapi.util.request(
			this.admin_private_endpoint + 'newsletter-group-endpoint',
			{ service: this.id, owner: this.owner },
			{ auth: true },
		);

		return response;
	}

	// One request returns every named group with its address and subscriber count, so the sender
	// cache is seeded from it. Asking per group would be one round trip per group. A failed listing
	// (a region without named groups yet) resolves to [] so the fixed groups still work.
	async listNewsletterGroups(refresh?: boolean): Promise<NewsletterGroup[]> {
		if (this._newsletterGroupsPromise && !refresh) {
			return await this._newsletterGroupsPromise;
		}

		this._newsletterGroupsPromise = (async () => {
			let response = await this.newsletterGroupEndpoint();
			let groups: NewsletterGroup[] = Array.isArray(response?.groups)
				? response.groups
				: [];

			for (let g of groups) {
				if (g?.endpoint) g.endpoint = nlNewsletterAddress(g.endpoint);
				if (g?.group && g?.endpoint) {
					this.newsletterSender[newsletterGroupToken(g.group)] =
						Promise.resolve(g.endpoint);
				}
			}

			this.newsletterGroups = groups;
			return groups;
		})();

		try {
			return await this._newsletterGroupsPromise;
		} catch (err) {
			// a failed listing must not be cached as the answer
			this._newsletterGroupsPromise = null;
			console.error('listNewsletterGroups failed', err);
			this.newsletterGroups = [];
			return [];
		}
	}

	// ------------------------------- vivians area -------------------

	async refresh(onlyServiceInfo = false): Promise<ServiceFromServer> {
		let service = await skapi.util.request(
			this.admin_private_endpoint + 'pull-services',
			{
				service: skapi.service,
				owner: skapi.owner,
				service_id: this.id,
				owner_id: this.owner,
				bypass_cached_service: true,
			},
			{ auth: true },
		);
		for (let region in service) {
			if (!Array.isArray(service[region])) {
				continue;
			}

			// Each region maps to an array of matching services, optionally
			// followed by skapi's 'end' startKey marker; empty regions come
			// back as ['end']. Pick the first real (object) entry rather than
			// blindly taking index 0, which may be the 'end' string.
			const fresh = service[region].find(
				(entry: any) => entry && typeof entry === 'object',
			);
			if (fresh) {
				// The API response may omit fields that were set
				// client-side (projectId). Preserve them so reactive
				// computeds that read these fields don't flicker false.
				if (!fresh.projectId && this.service?.projectId) {
					fresh.projectId = this.service.projectId;
				}
				this.service = fresh;
				break;
			}
		}

		if (onlyServiceInfo) {
			return this.service;
		}

		this.formattedSpecs = formatSpecs(this.service);

		// subdomain is null once it has been released (registerSubdomain() with no arg).
		const subdomain = this.service?.subdomain || '';
		let pendingStat =
			subdomain[0] === '+'
				? 'transit'
				: subdomain[0] === '*'
					? 'removing'
					: false;
		this.pending.subdomain = pendingStat;

		this.getSubdomainInfo();

		this._subsPromise = this.getStripeSubscription();

		return this.service;
	}

	static async load(srvc: ServiceFromServer): Promise<Service> {
		if (srvc && typeof srvc === 'object') {
			let {
				admin_private_endpoint,
				admin_public_endpoint,
				record_private_endpoint,
				record_public_endpoint,
			} = await ENDPOINTS;

			let inst = new Service(srvc, [
				admin_private_endpoint,
				record_private_endpoint,
				admin_public_endpoint,
				record_public_endpoint,
			]);

			// NON-BLOCKING: the project's own info is fully built by the ctor above.
			// The per-project Stripe subscription (_subsPromise, fired in the ctor)
			// is a RELATED-data fetch, don't block the project load (and the whole
			// list's Promise.all) on it. It resolves in the background; the
			// subscription-derived UI is re-applied reactively in addServiceToList.
			// (Also more robust: a Stripe error no longer fails the project load.)

			return inst;
		} else {
			throw new Error('Invalid service object.');
		}
	}
}

export async function createService(params: {
	name: string;
	description?: string;
	/**
	 * Where the project's data lives. Optional: left out, it falls back to the
	 * region closest to the visitor, which is what the picker preselects.
	 * A project can never be moved -- its region is the first four characters
	 * of the service id.
	 */
	region?: string;
}): Promise<ServiceFromServer> {
	if (!params?.name) throw new Error('Invalid service name.');

	let { admin_private_endpoint } = await ENDPOINTS;

	// A picked region only counts if this environment actually offers it; the
	// backend rejects anything else outright ("Region is not yet supported").
	let serviceRegion = resolveRegion(params.region, skapi.connection?.locale);

	let payload: { [key: string]: any } = {
		name: params.name,
		service: skapi.service,
		owner: skapi.owner,
		execute: 'create',
		region: serviceRegion,
	};

	if (params.description) {
		payload.description = params.description;
	}

	let service = await skapi.util.request(admin_private_endpoint + 'create-service', payload, {
		auth: true,
	});

	return service as ServiceFromServer;
}
